/**
 * ImageUpload Component
 *
 * Description: This component allows users to upload an image, displays a preview of the uploaded image,
 * and provides an option to remove the uploaded image. It uses a file input to handle the image upload
 * and a button to remove the image.
 *
 * Props:
 * - image (File | null): The currently uploaded image file.
 * - onImageChange (function): Function to call when the image is uploaded or removed.
 *
 * File Name: ImageUpload.tsx
 * Author: [Your Name]
 * Date: 27-07-2024
 */

import React, {
  ChangeEvent,
  DragEvent,
  useEffect,
  useId,
  useState,
} from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowUpFromBracket,
  faPlus,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import {
  convertToKebabCase,
  ImageTypeValidate,
} from "src/helpers/helperFunctions";
import imagePlus from "src/assets/images/admin-dashboard/imagePlus.png";
import { toast } from "react-toastify";

// Define a type for the image state
type ImageState = string | ArrayBuffer | null;

interface IImgaeUploadProps {
  image?: File | null | string;
  onImageChange: (file: File | null) => void;
  extraClasses?: string;
  circleImage?: boolean;
  allowedTypes?: string[];
  allowedExtensions?: string[];
  whilePrewiev?: string | undefined;
  newUi?: boolean;
  containerClasses?: string;
  newUiFrontend?: boolean;
  labelId?: string;
  id?: string;
  disabled?: boolean;
}

const ImageUpload: React.FC<IImgaeUploadProps> = ({
  image,
  onImageChange,
  extraClasses = "",
  circleImage = false,
  allowedExtensions = [".jpg", ".jpeg", ".png"],
  allowedTypes = ["image/png", "image/jpeg", "image/jpg"],
  whilePrewiev,
  newUi = false,
  newUiFrontend = false,
  containerClasses = "",
  labelId = "",
  id = "",
  disabled = false,
}) => {
  const uniqueId = useId();
  const [preview, setPreview] = useState<ImageState>(null);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    if (whilePrewiev) {
      setPreview(whilePrewiev);
    }
  }, [whilePrewiev]);

  const handleImageUpload = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.type === "application/pdf") {
        // Validate PDF file size (5MB limit)
        const maxSizeInBytes = 5 * 1000000; // 5MB in bytes
        if (file.size > maxSizeInBytes) {
          toast.error("File size exceeds the 5MB limit.");
          return;
        }
        if (!ImageTypeValidate({ file, allowedExtensions, allowedTypes , size : 5 })) {
          return;
        }
        setPreview(null);
        onImageChange(file);
      } else {
        processFile(file);
      }
    }
    event.target.value = "";
  };

  const handleRemoveImage = () => {
    onImageChange(null);
    setPreview(null);
  };

  const processFile = (file: File) => {
    if (!ImageTypeValidate({ file, allowedExtensions, allowedTypes , size : 5 })) {
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result);
    };
    reader.readAsDataURL(file);
    onImageChange(file);
  };

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    const file = event.dataTransfer.files[0];
    if (file) {
      if (file.type === "application/pdf") {
        // Validate PDF file size (5MB limit)
        const maxSizeInBytes = 5 * 1000000; // 5MB in bytes
        if (file.size > maxSizeInBytes) {
          toast.error("File size exceeds the 5MB limit.");
          return;
        }
        setPreview(null);
        onImageChange(file);
      } else {
        processFile(file);
      }
    }
  };

  useEffect(() => {
    if (image) {
      if (typeof image === "string") {
        setPreview(`${image}`);
      } else if (image instanceof File) {
        console.log("image.type ====> ", image.type);
        if (image.type === "application/pdf") {
          setPreview(null);
        } else {
          const reader = new FileReader();
          reader.onloadend = () => {
            setPreview(reader.result as string);
          };
          reader.readAsDataURL(image);
        }
      }
    } else {
      setPreview(null);
    }
  }, [image]);


  let allowedExtensionsString = "";
  if (allowedExtensions?.length > 1) {
    allowedExtensionsString = allowedExtensions.slice(0, -1).map((v) => v.replace(".", "").toUpperCase()).join(", ") + " and " + allowedExtensions[allowedExtensions.length - 1].replace(".", "").toUpperCase();
  } else {
    allowedExtensionsString = allowedExtensions?.map((v) => v.replace(".", "").toUpperCase()).join(", ");
  }

  return (
    <div className={`product-image-upload ${containerClasses}`}>
      <div
        className={`upload-label relative ${extraClasses} ${
          isDragging ? "dragging" : ""
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        style={{ pointerEvents: disabled ? "none" : "auto" }}
      >
        {(preview || image instanceof File) && (
          <button
            className="remove-image-button"
            id={`${labelId}-x-btn`}
            onClick={handleRemoveImage}
            style={{ pointerEvents: disabled ? "none" : "auto" }}
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        )}
        <label
          htmlFor={`imageUpload-${uniqueId}`}
          id={labelId || ""}
          style={{ pointerEvents: disabled ? "none" : "auto" }}
        >
          {preview || image instanceof File ? (
            <>
              {image instanceof File && image?.type === "application/pdf" ? (
                <div className="flex flex-col items-center justify-center p-4 w-full h-full">
                  <FontAwesomeIcon
                    icon={faArrowUpFromBracket}
                    className="mb-4 text-[#00443F]"
                    size="2xl"
                  />
                  <h3 className="text-[#00443F] font-Poppins-Medium font-size-18px mb-2">
                    PDF File Selected
                  </h3>
                  <p className="text-[#707070] font-Poppins-Medium font-size-14px text-center mb-2">
                    {image.name}
                  </p>
                  <p className="text-[#707070] font-Poppins-Medium font-size-12px text-center">
                    {(image.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
              ) : (
                <img
                  src={preview as string}
                  alt="Uploaded"
                  className={`uploaded-image ${
                    circleImage ? "rounded-full" : ""
                  }`}
                  id={`${labelId}-image`}
                />
              )}
            </>
          ) : (
            <div className="upload-placeholder">
              {newUi && (
                <div className="flex flex-col items-center justify-center w-[300px]">
                  <img src={imagePlus} alt="imagePlus" className="mb-2" />
                  <h2 className="text-[#878787] font-Poppins-Medium font-size-18px mb-1">
                    Upload Image
                  </h2>
                  <p className="text-[#707070] font-Poppins-Medium font-size-15px text-center">
                    Upload (Only {allowedExtensionsString} is supported up to a max file size of 5MB)
                  </p>
                </div>
              )}

              {newUiFrontend && (
                <div className="flex flex-col items-center justify-center w-[300px]">
                  <FontAwesomeIcon
                    icon={faArrowUpFromBracket}
                    className="mb-4 text-[#00443F]"
                    size="2xl"
                  />
                  <p className="text-[#707070] font-Poppins-Medium font-size-15px text-center">
                    Upload a file or drag and drop {allowedExtensionsString} up to 5MB
                  </p>
                </div>
              )}

              {!newUi && !newUiFrontend && <FontAwesomeIcon icon={faPlus} />}
            </div>
          )}
        </label>
      </div>
      <input
        type="file"
        id={`imageUpload-${uniqueId}`}
        accept={allowedTypes?.join(", ")}
        onChange={handleImageUpload}
        style={{ display: "none" }}
        disabled={disabled}
      />
    </div>
  );
};

export default ImageUpload;
